import type { CrewBriefingTemplate } from '@/api/client/models/index.ts';

function compareTemplateFileNames(left: string, right: string): number {
  const caseInsensitiveComparison = left.localeCompare(right, undefined, { sensitivity: 'base' });
  return caseInsensitiveComparison || left.localeCompare(right);
}

export function normalizeCrewBriefingTemplateFileName(
  fileName: string | null | undefined
): string | null {
  const normalizedFileName = fileName?.trim();
  if (!normalizedFileName || !normalizedFileName.toLowerCase().endsWith('.docx')) {
    return null;
  }

  const fileNameWithoutExtension = normalizedFileName.slice(0, -'.docx'.length);
  const invalidCharacters = '<>:"/\\|?*';
  const hasInvalidCharacter = Array.from(normalizedFileName).some(
    character => character.charCodeAt(0) < 32 || invalidCharacters.includes(character)
  );

  return fileNameWithoutExtension.trim() && !hasInvalidCharacter ? normalizedFileName : null;
}

export function getCrewBriefingTemplateFileNames(
  templates: CrewBriefingTemplate[]
): string[] {
  const fileNames = templates
    .map(template => normalizeCrewBriefingTemplateFileName(template.fileName))
    .filter((fileName): fileName is string => Boolean(fileName));

  return Array.from(new Set(fileNames)).sort(compareTemplateFileNames);
}

export function getLatestCrewBriefingTemplateFileName(
  templates: CrewBriefingTemplate[]
): string | null {
  const candidates = templates.flatMap(template => {
    const fileName = normalizeCrewBriefingTemplateFileName(template.fileName);
    const lastModifiedTime = template.lastModified?.getTime();

    return fileName && lastModifiedTime !== undefined && Number.isFinite(lastModifiedTime)
      ? [{ fileName, lastModifiedTime }]
      : [];
  });

  candidates.sort(
    (left, right) =>
      right.lastModifiedTime - left.lastModifiedTime ||
      compareTemplateFileNames(left.fileName, right.fileName)
  );

  return candidates[0]?.fileName ?? null;
}
